import { includes, snakeCase, values } from "lodash";
import { tr } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { Platform } from "@linkfluence/models/platform";
import { useFeature } from "@radarly/core/features/use-cases";

const METRIC_FETCHER = {
  [Metric.Doc]: (locale: Locale) => tr(locale, "METRICS::DOC"),
  [Metric.Impression]: (locale: Locale) => tr(locale, "METRICS::IMPRESSION"),
  [Metric.Reach]: (locale: Locale) => tr(locale, "METRICS::REACH"),
  [Metric.Repost]: (locale: Locale) => tr(locale, "METRICS::REPOST"),
  [Metric.Engagement]: (locale: Locale) => tr(locale, "METRICS::ENGAGEMENT"),
  [Metric.Rating]: (locale: Locale) => tr(locale, "METRICS::RATING"),
  [Metric.EngagementPerPost]: (locale: Locale) =>
    tr(locale, "METRICS::ENGAGEMENT-PER-POST"),
  [Metric.NetSentiment]: (locale: Locale) =>
    tr(locale, "METRICS::NET-SENTIMENT"),
};

export const getMetricLabel = (locale: Locale, metric: Metric) =>
  METRIC_FETCHER[metric] ? METRIC_FETCHER[metric](locale) : "";

export const getPlatformLabel = (locale: Locale, platform: Platform) =>
  {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const TWITTER_TO_X = useFeature("twitter_to_x");
    if (TWITTER_TO_X && platform === "twitter") {
      return "X";
    }
    return tr(locale, "PLATFORMS::" + platform);
  }

export const getLanguageLabel = (locale: Locale, language: string) =>
  tr(locale, "LANGUAGES::" + language);

export const getCountryLabel = (locale: Locale, country: string) =>
  tr(locale, "COUNTRIES::" + country);

const getAgeRangeLabel = (range: string): string =>
  ({
    "13-": "0-12",
    "13-17": "13-17",
    "18-24": "18-24",
    "25-34": "25-34",
    "35-44": "35-44",
    "45-54": "45-54",
    "55-64": "55-64",
    "65+": "65+",
  }[range]);

const getRatingLabel = (rating: number): string =>
  ({
    "0": "0 ★",
    "1": "1 ★",
    "1.5": "",
    "2": "2 ★",
    "2.5": "",
    "3": "3 ★",
    "3.5": "",
    "4": "4 ★",
    "4.5": "",
    "5": "5 ★",
  }[rating]);

const getTopicLabel = (locale: Locale, topic: string) =>
  tr(locale, "TOPICS::" + topic.replace(".", "_"));

export const getCategoryFacetName = (
  locale: Locale,
  facet: Facet,
  category: string
) => {
  if (facet === Facet.Countries) {
    if (!category) {
      return tr(locale, "COUNTRY_ZONES::INDEPENDANT");
    }
    return tr(locale, "COUNTRY_ZONES::" + category);
  }
  if (facet === Facet.Topics) {
    return tr(locale, "TOPICS::" + category);
  }
  return "";
};

// eslint-disable-next-line complexity
export const getFacetName = (locale: Locale, facet: Facet, id: any = "") => {
  if (id !== "") {
    if (facet === Facet.Languages) {
      return getLanguageLabel(locale, id);
    }
    if (facet === Facet.Platforms) {
      return getPlatformLabel(locale, id);
    }
    if (facet === Facet.Countries) {
      return getCountryLabel(locale, id);
    }
    if (facet === Facet.GeoLocations || facet === Facet.GeoTags) {
      return id;
    }
    if (facet === Facet.TwitterCertified) {
      return id
        ? tr(locale, "FACETS::CERTIFIED_ACCOUNT")
        : tr(locale, "FACETS::UNCERTIFIED_ACCOUNT");
    }
    if (facet === Facet.Favorites) {
      return id
        ? tr(locale, "FACETS::FAVOURITE_DOCUMENT")
        : tr(locale, "FACETS::NO_FAVOURITE_DOCUMENT");
    }
    if (facet === Facet.Favorite) {
      return id
        ? tr(locale, "FACETS::FAVORITE")
        : tr(locale, "FACETS::NOT_FAVORITE");
    }
    if (facet === Facet.Retweets) {
      return tr(locale, "FACETS::FLAG::RT_VALUES_TRUE");
    }
    if (facet === Facet.Retweeters) {
      return tr(locale, "FACETS::RETWEETERS_VALUES_TWITTER");
    }
    if (facet === Facet.UserUpdated) {
      return id
        ? tr(locale, "FACETS::FLAG::USERUPDATED_VALUES_BY_USER")
        : tr(locale, "FACETS::FLAG::USERUPDATED_VALUES_AUTOMATIC");
    }
    if (facet === Facet.AuthorAge) {
      return getAgeRangeLabel(id);
    }
    if (facet === Facet.AuthorBirthday) {
      return getAgeRangeLabel(id);
    }
    if (facet === Facet.ReviewRate) {
      return getRatingLabel(id);
    }
    if (facet === Facet.Topics) {
      return getTopicLabel(locale, id);
    }
    if (facet === Facet.SpamType) {
      return tr(locale, "FACETS::SPAMTYPE_SALE");
    }
    if (facet === Facet.ContentClassification) {
      return tr(locale, "FACETS::CONTENT_CLASSIFICATION_" + snakeCase(id));
    }
    if (facet === Facet.AuthorType) {
      return tr(
        locale,
        "FACETS::AUTHOR::VALUES::" + (id === "me" ? "ME" : "OTHER")
      );
    }
    return tr(locale, "FACETS::" + facet + "_" + id);
  }
  if (facet === Facet.SpamType) {
    return tr(locale, "FACETS::SPAMTYPE"); // No dash '-' in translation key
  }
  if (facet === Facet.DocIds) {
    return tr(locale, "FACETS::DOCIDS");
  }
  if (facet === Facet.AuthorType) {
    return tr(locale, "FACETS::AUTHORTYPE");
  }
  if (includes(values(Facet), facet)) {
    return tr(locale, "FACETS::" + facet);
  }
  return facet;
};
